import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import GenericTierTwo from "../GenericTierTwo";
import GenericModule from "../GenericModule";

import {
	initialValues,
	validationSchema,
	getSchemas,
	t2InitialValues,
} from "./aquaculture";
import { useAppSelector } from "@app/hooks";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import {
	useContextAwareGetAquaculture,
	useContextAwareGetAquacultureDefaults,
	useUpdateAquacultureMutation,
} from "@hooks/index";
import { selectCurrentProject } from "@features/project/projectSlice";
import type { ModuleFull } from "./aquacultureTypes";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import { selectCurrentBuilder } from "@features/builder/builderSlice";

const Aquaculture = () => {
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const { activeActivityId } = useAppSelector(selectCurrentProject);

	const id = moduleList.find(({ id }) => id === FEModules.Aquaculture)?.uniqueId;

	const { data: aquacultureData, isLoading, error, isError } = useContextAwareGetAquaculture(id ?? 0, { skip: !id, refetchOnMountOrArgChange: true });
	const [updateAquaculture, { error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate }] = useUpdateAquacultureMutation();
	const { data: defaults, error: defaultsError, isError: isDefaultsError, isLoading: isLoadingDefaults } = useContextAwareGetAquacultureDefaults(id ?? 0, { skip: !id });
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });

	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});
	const { errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });

	useEffect(() => {
		if (aquacultureData)
			setInitValues({
				module: aquacultureData.module,
				tiertwo: { ...aquacultureData.tiertwo, ...defaults },
			});
	}, [aquacultureData, defaults]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const payload = {
			data: { ...values },
			activityId: activeActivityId ?? 0,
		};

		try {
			values.module.id && (await updateAquaculture(payload).unwrap());
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.aquaculture"
					t2Schema={t2Schema}
					moduleSchema={moduleSchema}
					beModuleType={BEModulesClassNames.Aquaculture}
					isLoading={isLoadingUpdate}
					footerError={errorMsg}
					blockingError={blockingError}
					isError={isUpdateError}
				/>
				<GenericTierTwo
					title="project.aquaculture"
					t2Schema={t2Schema}
					warningMessage={defaultsErrorMsg || null}
				/>
			</>
		</Formik>
	);
};

export default Aquaculture;
